// src/pages/AboutPage.jsx
import { useEffect } from 'react';
import { Link } from 'react-router-dom';

const DISPLAY = "'Playfair Display', Georgia, serif";
const SANS    = "'DM Sans', 'DM Sans', system-ui, sans-serif";
const MONO    = "'IBM Plex Mono', 'IBM Plex Mono', 'Courier New', monospace";
const BG0     = "#060a0f";
const GREEN   = "#3ddc84";
const MUTED   = "#888";

const SectionLabel = ({ text }) => (
  <p style={{ fontFamily: SANS, fontSize: '0.7rem', fontWeight: 600, letterSpacing: '0.15em', color: MUTED, marginBottom: 24, textTransform: 'uppercase', margin: '0 0 24px' }}>
    {text}
  </p>
);

const NAV_LINKS = [
  { label: 'About', href: '/about' },
  { label: 'Pricing', href: '/pricing' },
  { label: 'FAQ', href: '/faq' },
];

function TopNav() {
  return (
    <nav style={{ position: 'fixed', top: 0, left: 0, right: 0, zIndex: 100, borderBottom: '1px solid #1a2a1f', background: 'rgba(6,10,15,0.95)', backdropFilter: 'blur(12px)', display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0 40px', height: 56 }}>
      <Link to="/" style={{ fontFamily: MONO, fontSize: '0.875rem', fontWeight: 600, color: GREEN, textDecoration: 'none', letterSpacing: '0.05em' }}>NUVPORT OS</Link>
      <div style={{ display: 'flex', gap: 32, alignItems: 'center' }}>
        {NAV_LINKS.map(l => (
          <Link key={l.href} to={l.href} style={{ fontFamily: SANS, fontSize: '0.875rem', fontWeight: 500, color: '#888', textDecoration: 'none' }}>{l.label}</Link>
        ))}
        <Link to="/" style={{ fontFamily: SANS, fontSize: '0.875rem', fontWeight: 600, color: '#fff', background: GREEN, padding: '8px 20px', textDecoration: 'none', color: '#060a0f' }}>Start Free</Link>
      </div>
    </nav>
  );
}

function PageFooter() {
  return (
    <footer style={{ borderTop: '1px solid #1a2a1f', padding: '60px 40px', background: BG0 }}>
      <div style={{ maxWidth: 1200, margin: '0 auto', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 40 }}>
        <div>
          <p style={{ fontFamily: MONO, fontSize: '0.875rem', fontWeight: 600, color: GREEN, marginBottom: 8, letterSpacing: '0.05em' }}>NUVPORT OS</p>
          <p style={{ fontFamily: SANS, fontSize: '0.8125rem', color: '#555', maxWidth: 240, lineHeight: 1.6 }}>
            The financial intelligence environment. Built for the 90% that Wall Street forgot.
          </p>
        </div>
        <div style={{ display: 'flex', gap: 60 }}>
          <div>
            <p style={{ fontFamily: SANS, fontSize: '0.7rem', fontWeight: 600, letterSpacing: '0.15em', color: '#444', marginBottom: 16, textTransform: 'uppercase' }}>Product</p>
            {[['About', '/about'], ['Pricing', '/pricing'], ['FAQ', '/faq']].map(([label, href]) => (
              <Link key={href} to={href} style={{ display: 'block', fontFamily: SANS, fontSize: '0.875rem', color: '#888', textDecoration: 'none', marginBottom: 10 }}>{label}</Link>
            ))}
          </div>
        </div>
      </div>
      <div style={{ maxWidth: 1200, margin: '40px auto 0', paddingTop: 32, borderTop: '1px solid #1a2a1f', display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: 16 }}>
        <p style={{ fontFamily: SANS, fontSize: '0.75rem', color: '#333' }}>© {new Date().getFullYear()} Nuvport OS. All rights reserved.</p>
        <p style={{ fontFamily: SANS, fontSize: '0.75rem', color: '#333' }}>Zagreb, Croatia · nuvportos.com</p>
      </div>
    </footer>
  );
}

export default function AboutPage() {
  useEffect(() => {
    document.title = 'About — Nuvport OS';
    window.scrollTo(0, 0);
  }, []);

  const story = [
    { bold: true, text: "I was learning to trade. I kept hitting the same wall." },
    { text: "Not a lack of effort. Not a lack of discipline. A lack of information. The right information. The kind that connects a geopolitical event on the other side of the world to the ETF sitting in my portfolio." },
    { text: "Every time I wanted to understand something deeply — why is this moving? what is my real exposure here? — I hit a wall. The tools that answered those questions cost $10,000 to $24,000 a year. They were built for institutions. Built for people who had already been let inside." },
    { italic: true, text: "I wasn't inside. And neither was anyone who looked like me." },
    { text: "So I built something. First for myself. A personal project. A tool that gave me what I needed to make better decisions. Then a friend looked at it and said: other people need this." },
    { bold: true, text: "He was right. That was the moment Nuvport stopped being a personal project and started being a mission." },
  ];

  return (
    <div style={{ background: BG0, minHeight: '100vh', color: '#ffffff' }}>
      <TopNav />

      {/* Hero */}
      <section style={{ padding: '140px 40px 80px', maxWidth: 800, margin: '0 auto' }}>
        <SectionLabel text="About Nuvport OS" />
        <h1 style={{ fontFamily: DISPLAY, fontSize: 'clamp(2.5rem, 5vw, 4rem)', fontWeight: 800, lineHeight: 1.1, marginBottom: 40, margin: '0 0 40px' }}>
          Built by someone who needed it.<br />
          Built for everyone who does.
        </h1>
        <div style={{ width: 48, height: 2, background: GREEN, marginBottom: 40 }} />
      </section>

      {/* The Story */}
      <section style={{ maxWidth: 800, margin: '0 auto', padding: '0 40px 80px' }}>
        <SectionLabel text="The Story" />
        {story.map((para, i) => (
          <p key={i} style={{
            fontFamily: SANS,
            fontSize: '1.0625rem',
            fontWeight: para.bold ? 600 : 400,
            fontStyle: para.italic ? 'italic' : 'normal',
            color: para.bold ? '#ffffff' : '#888',
            lineHeight: 1.75,
            marginBottom: 24,
          }}>
            {para.text}
          </p>
        ))}
      </section>

      {/* Pull quote */}
      <section style={{ borderTop: '1px solid #1a2a1f', borderBottom: '1px solid #1a2a1f', padding: '80px 40px' }}>
        <div style={{ maxWidth: 800, margin: '0 auto' }}>
          <p style={{ fontFamily: DISPLAY, fontSize: 'clamp(1.5rem, 3vw, 2.25rem)', fontStyle: 'italic', color: '#ffffff', lineHeight: 1.4, margin: 0 }}>
            "Trading is not gambling. It never was. Nobody just told the 90%."
          </p>
        </div>
      </section>

      {/* The Vision */}
      <section style={{ maxWidth: 800, margin: '0 auto', padding: '80px 40px' }}>
        <SectionLabel text="The Vision" />
        <h2 style={{ fontFamily: DISPLAY, fontSize: 'clamp(1.75rem, 3vw, 2.5rem)', fontWeight: 700, marginBottom: 32, margin: '0 0 32px' }}>
          Not a tool. An environment.
        </h2>
        {[
          "Right now there are two types of investors. The ones who can afford to know — institutions, hedge funds, elite university graduates with Bloomberg on every screen. And everyone else, making decisions on incomplete information.",
          "The gap between those two worlds is not intelligence. It is not discipline. It is not even money — though money is part of it. The gap is access to structured knowledge.",
          "Nuvport exists to close that gap. A living platform where your portfolio, your knowledge, and your strategy develop together. Where institutional-grade intelligence meets plain language explanation.",
        ].map((para, i) => (
          <p key={i} style={{ fontFamily: SANS, fontSize: '1.0625rem', color: '#888', lineHeight: 1.75, marginBottom: 24 }}>{para}</p>
        ))}
      </section>

      {/* Three phases */}
      <section style={{ borderTop: '1px solid #1a2a1f', padding: '80px 40px' }}>
        <div style={{ maxWidth: 1100, margin: '0 auto' }}>
          <SectionLabel text="The Roadmap" />
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 2 }}>
            {[
              { num: '01', name: 'Intelligence', status: 'Live Now', color: GREEN, desc: 'The data layer. 21 modules, institutional-grade analysis at $29/month.' },
              { num: '02', name: 'Knowledge', status: 'Next', color: '#4db8ff', desc: 'Data that explains itself. Geopolitical events connected to your portfolio.' },
              { num: '03', name: 'Environment', status: 'The Vision', color: '#c084fc', desc: 'One complete space. Learn, analyse, strategise, grow. From day one to year ten.' },
            ].map((phase) => (
              <div key={phase.num} style={{ background: '#0d1a14', padding: 40, borderTop: `2px solid ${phase.color}` }}>
                <p style={{ fontFamily: MONO, fontSize: '0.7rem', color: phase.color, letterSpacing: '0.1em', marginBottom: 8 }}>{phase.num}</p>
                <h3 style={{ fontFamily: DISPLAY, fontSize: '1.5rem', fontWeight: 700, color: '#ffffff', marginBottom: 8, margin: '0 0 8px' }}>{phase.name}</h3>
                <p style={{ fontFamily: MONO, fontSize: '0.7rem', color: '#555', letterSpacing: '0.08em', marginBottom: 20 }}>{phase.status.toUpperCase()}</p>
                <p style={{ fontFamily: SANS, fontSize: '0.875rem', color: '#888', lineHeight: 1.65 }}>{phase.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Founder note */}
      <section style={{ maxWidth: 800, margin: '0 auto', padding: '80px 40px 140px' }}>
        <div style={{ background: '#0d1a14', border: '1px solid #1a3028', padding: 48 }}>
          <SectionLabel text="A Note From The Founder" />
          <p style={{ fontFamily: DISPLAY, fontSize: '1.125rem', fontStyle: 'italic', color: '#ffffff', lineHeight: 1.7, marginBottom: 32 }}>
            "I am 19 years old. A global business student in Zagreb. I am dyslexic, I have no institutional background, and I built this on essentially no capital — with AI as my co-builder and sheer focus as my only resource. I saw a gap that billion-dollar companies deliberately left open. And I decided to fill it. Not because I was qualified. Because it needed to exist."
          </p>
          <div style={{ borderTop: '1px solid #1a3028', paddingTop: 24 }}>
            <p style={{ fontFamily: SANS, fontSize: '0.875rem', fontWeight: 600, color: '#ffffff', marginBottom: 4 }}>Founder, Nuvport OS</p>
            <p style={{ fontFamily: SANS, fontSize: '0.8125rem', color: '#888' }}>Zagreb, Croatia · 2025</p>
          </div>
        </div>
      </section>

      <PageFooter />
    </div>
  );
}
